/**
 * In-site WYSIWYG editor — owner/staff edit the live site (marketing + in-app)
 * in place: text, headings, images and pricing. Content is stored server-side
 * (/api/content) and merged OVER the code defaults, so every visitor sees the
 * latest copy while a staff user gets an "Edit site" bar to change it on the
 * page. Saves happen per-block on blur; AI copy suggestions and image upload
 * go through the same content API.
 *
 * Degrades safely: if the content map fails to load, or the viewer is not
 * staff, <Editable>/<EditableImg> simply render their children / default src
 * untouched — a visitor can never see an editing affordance and a fetch error
 * never blanks the page.
 *
 * Author: Maré Greyling (Thevertex) <mare.greyling@gmail.com> — https://mare-greyling.com
 */
import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { api } from '../api.js';
import { useAuth } from '../App.jsx';
import AIAction from './AIAction.jsx';

const Ctx = createContext(null);
const useEdit = () => useContext(Ctx);

/**
 * App-wide provider. Loads the content map once, exposes it plus the edit
 * state, and renders the floating Edit-site bar for staff only. Mount this
 * INSIDE the auth provider (it reads `me` to decide who may edit).
 */
export function EditProvider({ children }) {
  const { me } = useAuth();
  const canEdit = !!(me && (me.is_staff || me.is_superuser) && !me._cached);
  const [map, setMap] = useState({});
  const [editing, setEditing] = useState(false);
  const [sel, setSel] = useState(null); // currently focused block: {key, kind, el}

  useEffect(() => {
    let alive = true;
    api('/content/')
      .then((d) => { if (alive && d && typeof d === 'object') setMap(d); })
      .catch(() => { /* no CMS overrides yet — defaults stand */ });
    return () => { alive = false; };
  }, []);

  // Optimistic upsert: reflect locally at once, then persist. A failed save
  // keeps the on-screen change (the user still sees their edit) rather than
  // snapping back mid-session.
  const save = useCallback(async (key, kind, value) => {
    setMap((m) => ({ ...m, [key]: { kind, value } }));
    try { await api('/content/save/', { method: 'POST', body: { key, kind, value } }); }
    catch { /* surfaced elsewhere; keep optimistic value */ }
  }, []);

  const suggest = useCallback(async (text) => {
    const d = await api('/content/suggest/', { method: 'POST', body: { text } });
    return (d && d.suggestion) || '';
  }, []);

  const uploadImage = useCallback(async (file) => {
    const fd = new FormData();
    fd.append('file', file);
    const d = await api('/content/image/', { method: 'POST', form: fd });
    return (d && d.url) || '';
  }, []);

  const value = { canEdit, editing, setEditing, map, save, suggest, uploadImage, sel, setSel };
  return (
    <Ctx.Provider value={value}>
      {children}
      {canEdit && <EditBar />}
    </Ctx.Provider>
  );
}

/**
 * An editable block. `id` is the content key; `kind` is 'text' (plain) or
 * 'html' (keeps inline markup like <br> and <span>). `as` is the tag to
 * render. Children are the code default, shown until a stored value exists.
 */
export function Editable({ id, as: Tag = 'div', kind = 'text', className = '', children }) {
  const ctx = useEdit();
  const ref = useRef(null);
  const stored = ctx ? ctx.map[id]?.value : undefined;

  // Read-only render (visitor, non-staff, or edit mode off).
  if (!ctx || !ctx.canEdit || !ctx.editing) {
    if (stored == null) return <Tag className={className}>{children}</Tag>;
    if (kind === 'html') return <Tag className={className} dangerouslySetInnerHTML={{ __html: stored }} />;
    return <Tag className={className}>{stored}</Tag>;
  }

  const read = (el) => (kind === 'html' ? el.innerHTML : el.innerText);
  const onFocus = () => {
    const el = ref.current;
    if (!el) return;
    el.dataset.orig = read(el);          // baseline to diff against on blur
    ctx.setSel({ key: id, kind, el });
  };
  const onBlur = () => {
    const el = ref.current;
    if (!el) return;
    const val = read(el);
    if (val !== el.dataset.orig) ctx.save(id, kind, val);
  };

  const editCls =
    `${className} outline-none rounded-sm ring-1 ring-sky-500/30 hover:ring-sky-400/70 ` +
    'focus:ring-2 focus:ring-sky-400 cursor-text transition';
  const common = {
    ref, contentEditable: true, suppressContentEditableWarning: true,
    spellCheck: false, onFocus, onBlur, className: editCls, 'data-ekey': id,
  };
  // Editing render: stored value wins as the starting content, else the code
  // default becomes editable in place (its DOM is read back on blur).
  if (stored != null && kind === 'html') return <Tag {...common} dangerouslySetInnerHTML={{ __html: stored }} />;
  if (stored != null) return <Tag {...common}>{stored}</Tag>;
  return <Tag {...common}>{children}</Tag>;
}

/**
 * An editable image. Stores {src, w} JSON so a replaced picture and a
 * drag-resized width both survive. Read-only viewers just get the <img>.
 */
export function EditableImg({ id, src, alt = '', className = '', style }) {
  const ctx = useEdit();
  const raw = ctx ? ctx.map[id]?.value : undefined;
  let cur = src, width;
  if (raw) {
    try { const j = JSON.parse(raw); cur = j.src || src; width = j.w; }
    catch { cur = raw; }
  }
  const dims = width ? { ...style, width } : style;

  if (!ctx || !ctx.canEdit || !ctx.editing) {
    return <img src={cur} alt={alt} className={className} style={dims} />;
  }

  const pick = async (e) => {
    const f = e.target.files && e.target.files[0];
    if (!f) return;
    const url = await ctx.uploadImage(f);
    if (url) ctx.save(id, 'image', JSON.stringify({ src: url, w: width }));
  };
  const startResize = (e) => {
    e.preventDefault();
    const img = e.currentTarget.parentElement.querySelector('img');
    if (!img) return;
    const x0 = e.clientX;
    const w0 = img.getBoundingClientRect().width;
    const move = (ev) => { img.style.width = `${Math.max(40, Math.round(w0 + ev.clientX - x0))}px`; };
    const up = () => {
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup', up);
      ctx.save(id, 'image', JSON.stringify({ src: cur, w: img.style.width }));
    };
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
  };

  return (
    <span className="relative inline-block group">
      <img src={cur} alt={alt} className={className} style={dims} />
      <span className="absolute inset-0 ring-1 ring-sky-400/50 pointer-events-none rounded-sm" />
      <label className="absolute top-1 left-1 text-[10px] bg-black/70 text-white px-1.5 py-0.5 rounded cursor-pointer">
        Replace
        <input type="file" accept="image/*" className="hidden" onChange={pick} />
      </label>
      <span
        onMouseDown={startResize}
        title="Drag to resize"
        className="absolute -bottom-1 -right-1 w-3.5 h-3.5 bg-sky-400 border border-white rounded-sm cursor-nwse-resize"
      />
    </span>
  );
}

/**
 * The floating editor bar. Collapsed to an "Edit site" pill until turned on;
 * expanded it gives basic rich-text controls plus an AI rewrite of the
 * focused block. Toolbar buttons preventDefault on mousedown so the caret /
 * selection in the contentEditable block is never lost.
 */
function EditBar() {
  const ctx = useEdit();
  const { editing, setEditing, sel } = ctx;
  // Snapshot of the focused block before an AI rewrite, so Undo restores it.
  const before = useRef(null);

  // Edit-site affordance shows ONLY on the public homepage / coming-soon page.
  // Everywhere else (the whole in-app experience, maps, manage, etc.) there is
  // no edit bar — the live site is only editable where marketing copy lives.
  const { pathname } = useLocation();
  if (pathname !== '/' && pathname !== '/home-preview') return null;

  const exec = (cmd, arg) => (e) => {
    e.preventDefault();
    try { document.execCommand(cmd, false, arg); } catch { /* older browsers */ }
  };

  // AI rewrite of the focused block, standardised on the shared AIAction. The
  // block itself is contentEditable, so the result stays visible and editable
  // in place; Undo swaps the previous copy back and re-saves it.
  const runSuggest = async () => {
    if (!sel || !sel.el) throw { data: { detail: 'Click a block first, then ✨.' } };
    before.current = { key: sel.key, kind: sel.kind, el: sel.el,
                       prev: sel.kind === 'html' ? sel.el.innerHTML : sel.el.innerText };
    const out = await ctx.suggest(sel.el.innerText);
    return out || null;
  };
  const applySuggest = (out) => {
    const s0 = before.current; if (!s0) return;
    if (s0.kind === 'html') s0.el.innerHTML = out; else s0.el.innerText = out;
    ctx.save(s0.key, s0.kind, s0.kind === 'html' ? s0.el.innerHTML : out);
  };
  const undoSuggest = () => {
    const s0 = before.current; if (!s0) return;
    if (s0.kind === 'html') s0.el.innerHTML = s0.prev; else s0.el.innerText = s0.prev;
    ctx.save(s0.key, s0.kind, s0.prev);
  };

  if (!editing) {
    return (
      <button
        onClick={() => setEditing(true)}
        className="fixed bottom-24 right-6 z-[90] bg-sky-600 hover:bg-sky-500 text-white text-sm font-medium px-4 py-2 rounded-full shadow-lg"
      >
        ✎ Edit site
      </button>
    );
  }

  const btn = 'px-2.5 py-1 rounded hover:bg-white/10 text-sm disabled:opacity-40';
  return (
    <div className="fixed bottom-0 inset-x-0 z-[90] bg-slate-900/95 backdrop-blur border-t border-edge text-slate-200 px-3 py-2 flex items-center gap-1 flex-wrap shadow-2xl">
      <span className="text-xs text-slate-400 mr-2">Editing — click any highlighted text to change it</span>
      <button className={btn} onMouseDown={exec('bold')} title="Bold"><b>B</b></button>
      <button className={btn} onMouseDown={exec('italic')} title="Italic"><i>I</i></button>
      <button className={btn} onMouseDown={exec('formatBlock', 'H1')} title="Heading 1">H1</button>
      <button className={btn} onMouseDown={exec('formatBlock', 'H2')} title="Heading 2">H2</button>
      <button className={btn} onMouseDown={exec('formatBlock', 'P')} title="Paragraph">P</button>
      <span className="w-px h-5 bg-white/15 mx-1" />
      <AIAction size="sm" label="AI suggest"
                title="Rewrite the focused block with AI"
                disabled={!sel}
                run={runSuggest} onResult={applySuggest} onUndo={undoSuggest}
                readback="Rewrote the block — edit it in place; it saves as you go." />
      <div className="flex-1" />
      <button
        className="px-3 py-1 rounded bg-sky-600 hover:bg-sky-500 text-white text-sm"
        onClick={() => setEditing(false)}
      >
        Done
      </button>
    </div>
  );
}
